import React, { ReactNode } from "react";

interface SimpleCircularIndicatorProps {
  count: number;
  children?: ReactNode;
  color?: string;
  size?: number;
  right?: number;
  top?: number;
  standalone?: boolean;
  fullWidth?: boolean;
}

export const formatAlertCount = (count: number) =>
  count > 99 ? "99+" : String(count);

const getBadgeStyle = (
  count: number,
  size: number,
  color: string,
  right: number,
  standalone: boolean,
  top?: number
): React.CSSProperties => {
  const label = formatAlertCount(count);
  const isWideLabel = label.length >= 3;

  return {
    position: standalone ? "relative" : "absolute",
    top: standalone ? undefined : top ?? -size / 4,
    right: standalone ? undefined : right ? -right : -size / 4,
    minWidth: isWideLabel ? size + 12 : size,
    height: size,
    padding: isWideLabel ? "0 5px" : 0,
    backgroundColor: color,
    borderRadius: size / 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: isWideLabel ? Math.max(9, size * 0.42) : size * 0.55,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: isWideLabel ? "-0.03em" : undefined,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    zIndex: 3,
    pointerEvents: "none",
  };
};

const SimpleCircularIndicator: React.FC<SimpleCircularIndicatorProps> = ({
  count,
  children,
  color = "red",
  size = 20,
  right = 0,
  top,
  standalone = false,
  fullWidth = false,
}) => {
  if (count <= 0) {
    return standalone ? null : <>{children}</>;
  }

  const badge = (
    <div style={getBadgeStyle(count, size, color, right, standalone, top)}>
      {formatAlertCount(count)}
    </div>
  );

  if (standalone) {
    return badge;
  }

  return (
    <div
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-flex",
        width: fullWidth ? "100%" : undefined,
        // Keep badge above neighboring tabs / active tab backgrounds
        zIndex: fullWidth ? 2 : 5,
      }}
    >
      {children}
      {badge}
    </div>
  );
};

export default SimpleCircularIndicator;
